import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, CheckCircle2, History, RotateCcw, X, Send, Trash2, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSiteContent() {
  const [slots, setSlots] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // { key: "draft"|"publish"|"discard" }
  const [flash, setFlash] = useState({}); // { key: "draft-saved"|"published" }
  const [historyKey, setHistoryKey] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API}/admin/site-content`, { withCredentials: true })
      .then((r) => {
        setSlots(r.data);
        // Prefill textarea with the draft if there is one, otherwise the published value.
        const initial = {};
        r.data.forEach((s) => {
          initial[s.key] = s.draft_value != null && s.draft_value !== "" ? s.draft_value : s.value;
        });
        setDrafts(initial);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load site content"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const setSlotBusy = (key, mode) => setBusy((prev) => ({ ...prev, [key]: mode }));
  const clearSlotBusy = (key) => setBusy((prev) => { const n = { ...prev }; delete n[key]; return n; });
  const flashSlot = (key, kind) => {
    setFlash((prev) => ({ ...prev, [key]: kind }));
    setTimeout(() => setFlash((prev) => { const n = { ...prev }; delete n[key]; return n; }), 2000);
  };

  const patchSlot = async (key, publish) => {
    setSlotBusy(key, publish ? "publish" : "draft");
    try {
      const r = await axios.patch(
        `${API}/admin/site-content/${encodeURIComponent(key)}`,
        { value: drafts[key] ?? "", publish },
        { withCredentials: true },
      );
      const nextValue = publish ? r.data?.value ?? "" : undefined;
      const nextDraft = publish ? null : r.data?.draft_value ?? "";
      setSlots((prev) => prev.map((s) => {
        if (s.key !== key) return s;
        return {
          ...s,
          ...(publish ? { value: nextValue, draft_value: null } : { draft_value: nextDraft }),
        };
      }));
      toast.success(publish ? "Published" : "Draft saved");
      flashSlot(key, publish ? "published" : "draft-saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      clearSlotBusy(key);
    }
  };

  const discardDraft = async (key) => {
    setSlotBusy(key, "discard");
    try {
      await axios.delete(
        `${API}/admin/site-content/${encodeURIComponent(key)}/draft`,
        { withCredentials: true },
      );
      setSlots((prev) => prev.map((s) => (s.key === key ? { ...s, draft_value: null } : s)));
      setDrafts((prev) => {
        const slot = slots.find((s) => s.key === key);
        return { ...prev, [key]: slot ? slot.value : "" };
      });
      toast.success("Draft discarded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Discard failed");
    } finally {
      clearSlotBusy(key);
    }
  };

  const openHistory = async (key) => {
    setHistoryKey(key);
    setHistoryLoading(true);
    setHistoryRows([]);
    try {
      const r = await axios.get(
        `${API}/admin/site-content/${encodeURIComponent(key)}/history`,
        { withCredentials: true },
      );
      setHistoryRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const restore = async (snapshotId) => {
    if (!historyKey) return;
    setRestoring(snapshotId);
    try {
      const r = await axios.post(
        `${API}/admin/site-content/${encodeURIComponent(historyKey)}/restore`,
        { snapshot_id: snapshotId },
        { withCredentials: true },
      );
      const restored = r.data?.value ?? "";
      setSlots((prev) => prev.map((s) => (s.key === historyKey ? { ...s, value: restored, draft_value: null } : s)));
      setDrafts((prev) => ({ ...prev, [historyKey]: restored }));
      toast.success("Restored");
      setHistoryKey(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(null);
    }
  };

  return (
    <div data-testid="admin-site-content">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Site content</p>
          <h1 className="mt-3 font-display text-3xl md:text-4xl leading-tight text-[#FAF9F6]">
            Edit the copy on the public pages
          </h1>
          <p className="mt-3 text-sm text-[#A9A9A6] max-w-2xl">
            Each block controls a slot on the site. <strong className="text-[#FAF9F6]">Save draft</strong> holds
            your changes privately; <strong className="text-[#FAF9F6]">Publish</strong> pushes them live and
            snapshots the previous value.
          </p>
        </div>
        <a
          href="/?draft=1"
          target="_blank"
          rel="noreferrer"
          data-testid="site-content-preview-drafts-link"
          className="inline-flex items-center gap-1.5 border border-[#D8A936] text-[#D8A936] px-3 py-2 text-[11px] tracking-[0.14em] uppercase hover:bg-[#D8A936] hover:text-[#0B0B0C] transition-colors"
        >
          <ExternalLink size={12} />
          Preview drafts
        </a>
      </div>

      {loading && <p className="text-sm text-[#A9A9A6]">Loading…</p>}

      <div className="space-y-6">
        {slots.map((slot) => {
          const val = drafts[slot.key] ?? "";
          const stored = slot.draft_value != null && slot.draft_value !== "" ? slot.draft_value : slot.value;
          const dirty = val !== stored;
          const hasDraft = slot.draft_value != null && slot.draft_value !== "";
          const busyMode = busy[slot.key];
          const flashState = flash[slot.key];
          let statusLabel = "In sync";
          let statusClass = "text-[#6E6E6B]";
          if (dirty) { statusLabel = "Unsaved changes"; statusClass = "text-[#D8A936]"; }
          else if (hasDraft) { statusLabel = "Draft pending"; statusClass = "text-[#D8A936]"; }
          return (
            <div
              key={slot.key}
              data-testid={`site-content-row-${slot.key}`}
              className="border border-[#343437] bg-[#151517]/60 p-5 lg:p-6"
            >
              <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="font-display text-[13px] tracking-[0.14em] uppercase text-[#FAF9F6]">
                    {slot.label}
                  </p>
                  {hasDraft && (
                    <span
                      data-testid={`site-content-draft-badge-${slot.key}`}
                      className="inline-flex items-center gap-1 border border-[#D8A936]/60 text-[#D8A936] px-2 py-0.5 text-[10px] tracking-[0.14em] uppercase"
                    >
                      Draft
                    </span>
                  )}
                </div>
                <code className="text-[10px] text-[#6E6E6B]">{slot.key}</code>
              </div>
              {slot.hint && (
                <p className="text-xs text-[#A9A9A6] leading-relaxed mb-3">{slot.hint}</p>
              )}
              <textarea
                value={val}
                onChange={(e) => setDrafts((prev) => ({ ...prev, [slot.key]: e.target.value }))}
                data-testid={`site-content-input-${slot.key}`}
                rows={Math.min(8, Math.max(3, val.split("\n").length + 1))}
                className="w-full bg-[#0B0B0C] border border-[#343437] px-3 py-2.5 text-sm text-[#FAF9F6] outline-none focus:border-[#D8A936] transition-colors font-mono resize-y"
              />
              {hasDraft && (
                <p className="mt-2 text-[10px] tracking-[0.14em] uppercase text-[#6E6E6B]">
                  Published value:
                  <span className="text-[#A9A9A6] normal-case tracking-normal ml-2 font-mono">
                    {slot.value.length > 90 ? `${slot.value.slice(0, 90)}…` : slot.value}
                  </span>
                </p>
              )}
              <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                <span className={`text-[10px] tracking-[0.14em] uppercase ${statusClass}`}>
                  {statusLabel}
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => openHistory(slot.key)}
                    data-testid={`site-content-history-${slot.key}`}
                    className="inline-flex items-center gap-1.5 border border-[#343437] px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase text-[#A9A9A6] hover:text-[#FAF9F6] hover:border-[#6E6E6B] transition-colors"
                  >
                    <History size={12} />
                    History
                  </button>
                  {hasDraft && (
                    <button
                      type="button"
                      onClick={() => discardDraft(slot.key)}
                      disabled={busyMode === "discard"}
                      data-testid={`site-content-discard-${slot.key}`}
                      className="inline-flex items-center gap-1.5 border border-[#343437] px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase text-[#A9A9A6] hover:text-[#FAF9F6] hover:border-[#6E6E6B] transition-colors disabled:opacity-40"
                    >
                      {busyMode === "discard" ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      Discard draft
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => patchSlot(slot.key, false)}
                    disabled={!dirty || !!busyMode}
                    data-testid={`site-content-save-draft-${slot.key}`}
                    className="inline-flex items-center gap-1.5 border border-[#D8A936] text-[#D8A936] px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase hover:bg-[#D8A936] hover:text-[#0B0B0C] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {busyMode === "draft" ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : flashState === "draft-saved" ? (
                      <CheckCircle2 size={12} />
                    ) : (
                      <Save size={12} />
                    )}
                    {busyMode === "draft" ? "Saving…" : flashState === "draft-saved" ? "Saved" : "Save draft"}
                  </button>
                  <button
                    type="button"
                    onClick={() => patchSlot(slot.key, true)}
                    disabled={(!dirty && !hasDraft) || !!busyMode}
                    data-testid={`site-content-publish-${slot.key}`}
                    className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {busyMode === "publish" ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : flashState === "published" ? (
                      <CheckCircle2 size={14} />
                    ) : (
                      <Send size={14} />
                    )}
                    {busyMode === "publish" ? "Publishing…" : flashState === "published" ? "Live" : "Publish"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {historyKey && (
        <div
          data-testid="site-content-history-modal"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
          onClick={() => setHistoryKey(null)}
        >
          <div
            className="bg-[#0B0B0C] border border-[#343437] w-full max-w-2xl my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-[#343437]">
              <div>
                <p className="eyebrow">History</p>
                <p className="text-sm text-[#FAF9F6] font-mono mt-1">{historyKey}</p>
              </div>
              <button
                type="button"
                onClick={() => setHistoryKey(null)}
                data-testid="site-content-history-close"
                className="text-[#A9A9A6] hover:text-[#FAF9F6] transition-colors"
                aria-label="Close history"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 max-h-[70vh] overflow-y-auto">
              {historyLoading && <p className="text-sm text-[#A9A9A6]">Loading history…</p>}
              {!historyLoading && historyRows.length === 0 && (
                <p className="text-sm text-[#A9A9A6]">No snapshots yet. Every publish creates one.</p>
              )}
              <div className="space-y-3">
                {historyRows.map((row) => (
                  <div
                    key={row.id}
                    data-testid={`site-content-history-row-${row.id}`}
                    className="border border-[#343437] bg-[#151517]/60 p-4"
                  >
                    <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
                      <div className="flex items-center gap-2 text-[10px] tracking-[0.14em] uppercase text-[#6E6E6B]">
                        <span>{new Date(row.created_at).toLocaleString()}</span>
                        <span>·</span>
                        <span>{row.edited_by}</span>
                        {row.reason === "restore" && (
                          <>
                            <span>·</span>
                            <span className="text-[#D8A936]">from restore</span>
                          </>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => restore(row.id)}
                        disabled={restoring === row.id}
                        data-testid={`site-content-history-restore-${row.id}`}
                        className="inline-flex items-center gap-1.5 border border-[#D8A936] text-[#D8A936] px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase hover:bg-[#D8A936] hover:text-[#0B0B0C] transition-colors disabled:opacity-40"
                      >
                        {restoring === row.id ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <RotateCcw size={12} />
                        )}
                        Restore
                      </button>
                    </div>
                    <pre className="text-xs text-[#FAF9F6] whitespace-pre-wrap font-mono leading-relaxed">{row.value}</pre>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
